import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchTrips, fetchInvitations, respondToInvitation, signOut, signOutEverywhere,
} from '../lib/api.js';
import { rangeLabel } from '../lib/format.js';
import Icon from '../components/Icon.jsx';
import ErrorState from '../components/ErrorState.jsx';

const STATUS = {
  planning: 'in preparazione', upcoming: 'in arrivo',
  active: 'in corso', past: 'concluso', archived: 'archiviato',
};

// An invitation belongs here rather than inside a trip, because it IS a trip
// you do not have yet — there is no trip to open it from. It reads as a
// proposal and not as an itinerary on purpose: everything on the card was
// written by somebody else's account, so it says who sent it and asks, instead
// of showing their words as if they were yours.
function Invitations({ list, onAnswered }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const answer = async (inv, accept) => {
    setError(null);
    setBusy(inv.trip_id);
    try {
      await respondToInvitation(inv.trip_id, accept);
      onAnswered();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="section">
      <h2>Inviti {list.length > 1 && <span className="count">{list.length}</span>}</h2>
      {error && <div className="empty" role="alert">{error.message}</div>}
      <div className="rows">
        {list.map((inv) => (
          <div className="row invite" key={inv.trip_id}>
            <span className="body">
              <b>{inv.title}</b>
              <small>
                da {inv.invited_by ?? 'un altro account'}
                {inv.role === 'viewer' ? ' · sola lettura' : ''}
              </small>
              {(inv.start_date || inv.destination) && (
                <small>
                  {rangeLabel(inv.start_date, inv.end_date)}
                  {inv.destination && ` · ${inv.destination}`}
                </small>
              )}
            </span>
            <span className="invite-actions">
              <button className="btn" disabled={busy === inv.trip_id}
                      onClick={() => answer(inv, true)}>Accetta</button>
              <button className="btn quiet" disabled={busy === inv.trip_id}
                      onClick={() => answer(inv, false)}>Rifiuta</button>
            </span>
          </div>
        ))}
      </div>
      <p className="hint">
        Finché non accetti, il viaggio non compare da nessuna parte. Rifiutare
        lo elimina del tutto.
      </p>
    </div>
  );
}

export default function Trips() {
  const nav = useNavigate();
  const [trips, setTrips] = useState(null);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    fetchTrips().then(setTrips).catch(setError);
    // Never fatal: an older database has no such function, and a trip list that
    // works is worth more than an invitations panel that does.
    fetchInvitations().then(setInvites).catch(() => setInvites([]));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="shell">
      <div className="topbar">
        <div className="grow">
          <h1>I nostri viaggi</h1>
        </div>
        <button className="iconbtn" aria-label="Esci" onClick={() => signOut()}>
          <Icon name="logout" size={16} />
        </button>
      </div>

      <div className="page">
        {error && <ErrorState error={error} onRetry={load} />}
        {!trips && !error && <div className="spinner" aria-label="Caricamento" />}

        {invites.length > 0 && <Invitations list={invites} onAnswered={load} />}

        {trips?.length === 0 && invites.length === 0 && (
          <div className="empty">
            <b>Ancora nessun viaggio</b>
            Chiedi a Claude di crearne uno: «prendi queste conferme e costruisci
            il viaggio». Comparirà qui.
          </div>
        )}

        {trips?.length > 0 && (
          <div className="triplist">
            {trips.map((t) => (
              <button className="tripcard" key={t.id} onClick={() => nav(`/trip/${t.id}`)}>
                <span className="status">{STATUS[t.status] ?? t.status}</span>
                <h2>{t.title}</h2>
                <div className="when">
                  {rangeLabel(t.start_date, t.end_date)}
                  {t.start_date && ` ${t.start_date.slice(0, 4)}`}
                  {t.destination && ` · ${t.destination}`}
                </div>
                {t.summary && <div className="sum">{t.summary}</div>}
              </button>
            ))}
          </div>
        )}

        {trips?.length > 0 && (
          <div className="section">
            <button className="btn quiet" onClick={() => signOutEverywhere()}>
              Disconnetti tutti i dispositivi
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
